import { useContext, useState, useEffect } from 'react'
import { X } from 'lucide-react'
import CollabPickSession from './CollabPickSession'
import CollabSessionModal from './CollabSessionModal'
import CollabSessionContext from '../../../../contexts/CollabSessionContext'
import { sessionsSocket } from '../../../../api/socket'
import { toast } from '../../../ui/new/toaster'
import { useSearchParams } from 'react-router-dom'
import { uniqueNamesGenerator, adjectives, animals } from 'unique-names-generator'
import useSession from '../../../../hooks/useSession'
import { Dialog } from '../../../ui/new/dialog'
import { Button } from '../../../ui/new/button'

const generateUniqueId = () => Date.now()

type Props = {
  isOpen: boolean
  closeModal: () => void
}

const CollabModal = ({ isOpen, closeModal }: Props) => {
  const { sessions, setSessions, currentSessionId, setCurrentSessionId } = useContext(CollabSessionContext)
  const currentSession = sessions.find((s) => s.id === currentSessionId) || null
  const [searchParams] = useSearchParams()
  const { signedIn: userSignedIn, user } = useSession()

  useEffect(() => {
    if (searchParams.has('session')) {
      const sessionId = searchParams.get('session')!
      handleStartSession(sessionId)
    }
  }, [])

  // TODO: Remove this
  const [interval, setInt] = useState<number | null>(null)
  const [uid] = useState(generateUniqueId())
  useEffect(() => {
    if (!currentSessionId) {
      if (interval) clearInterval(interval!)
      setInt(null)
      return
    }

    sessionsSocket.on('ping', (data) => {
      // eslint-disable-next-line no-console
      console.log('Received ping', data['id'])
    })

    setInt(
      setInterval(() => {
        sessionsSocket.emit('ping', { id: uid })
        // eslint-disable-next-line no-console
        console.log('Sent ping', uid)
      }, 1000),
    )
  }, [currentSessionId])

  const updatedSession = (sessionId: string, sessionInfo: any) => {
    setSessions((prevSessions) =>
      prevSessions.map((session) =>
        session.id === sessionId ? { ...session, participants: sessionInfo['participants'] } : session,
      ),
    )
  }

  const handleUnexpectedDisconnect = () => {
    setCurrentSessionId(null)
    toast({ title: 'Foste desconectado inesperadamente', description: 'Por favor, tenta novamente mais tarde.' })
  }

  const addSocketListeners = (socket) => {
    socket.on('disconnect', handleUnexpectedDisconnect)
    socket.on('update_session_info', (data) => updatedSession(data['session_id'], data['session_info']))
  }

  const getName = () => {
    if (userSignedIn) {
      return user.name
    } else {
      return uniqueNamesGenerator({
        dictionaries: [adjectives, animals],
        length: 2,
        separator: '',
        style: 'capital',
      })
    }
  }

  const handleStartSession = (sessionId) => {
    sessionsSocket.sessionId = sessionId
    sessionsSocket
      .connect(getName())
      .then((sessionsSocket) => {
        const newSession = {
          id: sessionsSocket.sessionId,
          name: Math.random().toString(36).substr(2, 9),
          lastEdited: Date.now(),
          expirationTime: new Date(sessionsSocket.sessionInfo['expiration_time']).getTime(),
          currentUser: 'AnotherUser',
          link: `${window.location.origin}/planner?session=${sessionsSocket.sessionId}`,
          participants: sessionsSocket.sessionInfo['participants'],
        }

        setSessions((prevSessions) => [...prevSessions.filter((session) => session.id !== sessionId), newSession])

        addSocketListeners(sessionsSocket)
        setCurrentSessionId(sessionsSocket.sessionId)

        toast({ title: 'Entrou na sessão', description: 'Convida mais pessoas para se juntarem!' })
      })
      .catch(() => toast({ title: 'Erro ao entrar na sessão', description: 'Tente novamente mais tarde.' }))
  }

  const handleCreateSession = () => {
    sessionsSocket.sessionId = null
    sessionsSocket
      .connect(getName())
      .then((sessionsSocket) => {
        const newSession = {
          id: sessionsSocket.sessionId,
          name: Math.random().toString(36).substr(2, 9),
          lastEdited: Date.now(),
          expirationTime: sessionsSocket.sessionInfo['expiration_time'],
          link: `${window.location.origin}/planner?session=${sessionsSocket.sessionId}`,
          participants: sessionsSocket.sessionInfo['participants'],
        }

        addSocketListeners(sessionsSocket)
        setCurrentSessionId(sessionsSocket.sessionId)
        setSessions((prevSessions) => [...prevSessions, newSession])

        toast({ title: 'Sessão criada', description: 'Convida mais pessoas para se juntarem!' })
      })
      .catch(() => toast({ title: 'Erro ao criar a sessão', description: 'Tente novamente mais tarde.' }))
  }

  const handleExitSession = () => {
    sessionsSocket.off('disconnect', handleUnexpectedDisconnect)
    sessionsSocket.disconnect()
    toast({
      title: 'Sessão abandonada',
      description: 'Podes voltar a ela mais tarde, ou iniciar/entrar noutra sessão.',
    })
    setCurrentSessionId(null)
  }

  const handleDeleteSession = (sessionId: string | null) => {
    setSessions((prevSessions) => prevSessions.filter((session) => session.id !== sessionId))
    if (currentSession?.id === sessionId) {
      handleExitSession()
    }
  }

  const handleUpdateUser = (updatedName: string) => {
    if (!currentSession) return

    const updatedSession = {
      ...currentSession,
      participants: currentSession.participants.map((participant) =>
        participant.client_id === sessionsSocket.clientId ? { ...participant, name: updatedName } : participant,
      ),
    }
    setSessions((prevSessions) =>
      prevSessions.map((session) => (session.id === currentSession.id ? updatedSession : session)),
    )

    sessionsSocket.emit('update_participant', { name: updatedName })
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) closeModal()
      }}
    >
      <Dialog.Content className="max-w-2xl p-6">
        <div className="flex justify-end">
          <Dialog.Close asChild>
            <Button variant="ghost" size="md" square>
              <X size="18" />
            </Button>
          </Dialog.Close>
        </div>

        {currentSessionId === null && (
          <CollabPickSession
            sessions={sessions}
            onStartSession={handleStartSession}
            onCreateSession={handleCreateSession}
            onDeleteSession={handleDeleteSession}
          />
        )}

        {currentSessionId !== null && (
          <CollabSessionModal
            session={currentSession}
            onExitSession={handleExitSession}
            onUpdateUser={handleUpdateUser}
          />
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default CollabModal
